// services/training.js
import { notifySessionExpired } from "./session";
import { api, getToken } from "./auth";

const API_BASE =
  (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_BASE) ||
  process.env.REACT_APP_API_BASE ||
  "http://localhost:8000";

// 공통: 쿼리스트링 빌더 (빈 값 제거)
function buildQS(params = {}) {
  const entries = [];
  for (const [key, value] of Object.entries(params)) {
    if (value === "" || value === null || value === undefined) continue;
    if (Array.isArray(value)) {
      value.forEach((v) => {
        if (v === "" || v === null || v === undefined) return;
        entries.push([key, v]);
      });
    } else {
      entries.push([key, value]);
    }
  }
  const qs = new URLSearchParams(entries).toString();
  return qs ? `?${qs}` : "";
}

// 공통: JSON 요청 헬퍼 (서버가 text 반환해도 에러 메시지 보존)
async function requestJSON(url, options = {}) {
  const res = await fetch(url, {
    ...options,
    headers: {
      ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
      ...(options.headers || {}),
    },
  });
  const text = await res.text().catch(() => "");
  const statusMessage = `HTTP ${res.status}`;
  const shouldNotify = [401, 403].includes(res.status);

  if (!res.ok) {
    let errorMessage = text || statusMessage;
    if (text) {
      try {
        const j = JSON.parse(text);
        errorMessage = j?.detail || j?.message || errorMessage;
      } catch {
        // fallthrough
      }
    }
    if (shouldNotify) {
      const sessionMsg = "다시 로그인하세요.";
      notifySessionExpired(sessionMsg);
      errorMessage = sessionMsg;
    }
    throw new Error(errorMessage);
  }

  if (!text) return {};
  try {
    return JSON.parse(text);
  } catch (e) {
    throw new Error(`Invalid JSON: ${String(e)}\n${text.slice(0, 200)}`);
  }
}

/** 학습 데이터 목록 */
export async function listTraindata(params = {}) {
  const qs = buildQS(params);
  return requestJSON(`${API_BASE}/api/training/traindata${qs}`, { method: "GET" });
}

export async function downloadTraindataTemplate() {
  const res = await fetch(`${API_BASE}/api/training/traindata/template`, {
    method: "GET",
    headers: {
      ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}),
    },
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    let message = text || `HTTP ${res.status}`;
    if (text) {
      try {
        const json = JSON.parse(text);
        message = json?.detail || json?.message || message;
      } catch {
        // ignore
      }
    }
    throw new Error(message);
  }
  return res.blob();
}

/** 학습 데이터 수정 (백엔드가 PUT이면 유지) */
export async function updateTraindata(id, body) {
  return requestJSON(`${API_BASE}/api/training/traindata/${id}`, {
    method: "PUT", // 백엔드가 PATCH면 여기만 "PATCH"로 변경
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
}

/** 학습 요청 생성 */
export async function createTrainingRequest(payload) {
  return requestJSON(`${API_BASE}/api/training/requests`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

/** 학습 요청 목록 조회 */
export async function listTrainingRequests(params = {}) {
  const qs = buildQS(params);
  return requestJSON(`${API_BASE}/api/training/requests${qs}`, { method: "GET" });
}

/** 학습 요청 상세 조회 */
export async function getTrainingRequestDetail(requestId) {
  return requestJSON(`${API_BASE}/api/training/requests/${requestId}`, { method: "GET" });
}

/** 학습 요청 신규 데이터 목록 */
export async function listTrainingRequestNewData(requestId, params = {}) {
  const qs = buildQS(params);
  return requestJSON(`${API_BASE}/api/training/requests/${requestId}/new-traindata${qs}`, {
    method: "GET",
  });
}

export async function listMyTrainingRequests() {
  return requestJSON(`${API_BASE}/api/training/requests/mine`, {
    method: "GET",
  });
}

/** 학습 요청 승인 + 학습 실행 */
export async function approveTrainingRequest(requestId) {
  return requestJSON(`${API_BASE}/api/training/requests/${requestId}/approve-and-run`, {
    method: "POST",
  });
}

/** 학습 요청 반려 */
export async function rejectTrainingRequest(requestId, reason) {
  return requestJSON(`${API_BASE}/api/training/requests/${requestId}/reject`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ reason }),
  });
}

/** 학습 실행 로그 목록 */
export async function listRuns() {
  return requestJSON(`${API_BASE}/api/training/logs`, { method: "GET" });
}

/** 학습 실행 로그 상세 */
export async function getRunDetail(runId) {
  return requestJSON(`${API_BASE}/api/training/logs/${runId}`, { method: "GET" });
}

/**  학습데이터 생성: 반드시 API_BASE 사용 (상대경로 금지) */
export async function createTraindata(payload) {
  return requestJSON(`${API_BASE}/api/training/traindata`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

/**  예측 히스토리 조회: 백엔드 스펙(/api/predict/history)로 통일 */
export async function listPredictions(params = {}) {
  // 지원 파라미터 예: { run_id, q, page=1, page_size=20 }
  const qs = buildQS({
    ...params,
    _ts: Date.now(), // 캐시 버스팅
  });
  return requestJSON(`${API_BASE}/api/predict/history${qs}`, { method: "GET" });
}

export async function importTraindata(formData) {
  try {
    const response = await api.post("/api/training/traindata/import", formData, {
      headers: { "Content-Type": "multipart/form-data" },
    });
    return response.data;
  } catch (error) {
    const status = error?.response?.status;
    let message =
      error?.response?.data?.detail ||
      error?.response?.data?.message ||
      error?.message ||
      `HTTP ${status}`;
    if ([401, 403].includes(status)) {
      const sessionMsg = "다시 로그인하세요.";
      notifySessionExpired(sessionMsg);
      message = sessionMsg;
    }
    throw new Error(message);
  }
}

// services/training.js 최하단 근처에 추가
export async function deleteTraindata(id) {
  const res = await fetch(`${API_BASE}/api/training/traindata/${id}`, {
    method: "DELETE",
    headers: { ...(getToken() ? { Authorization: `Bearer ${getToken()}` } : {}) },
  });
  const text = await res.text().catch(() => "");
  const statusMessage = `HTTP ${res.status}`;
  const shouldNotify = [401, 403].includes(res.status);

  if (!res.ok) {
    let errorMessage = text || statusMessage;
    if (text) {
      try {
        const j = JSON.parse(text);
        errorMessage = j?.detail || j?.message || errorMessage;
      } catch {
        // fallthrough
      }
    }
    if (shouldNotify) {
      const sessionMsg = "다시 로그인하세요.";
      notifySessionExpired(sessionMsg);
      errorMessage = sessionMsg;
    }
    throw new Error(errorMessage);
  }
  return true;
}
